import styled from '@emotion/styled';
import { Divider, IconButton, Tooltip, tooltipClasses, TooltipProps } from '@mui/material';
import React, { FC, useEffect, useRef, useState } from 'react';
import { Icons } from '../../../../assets/SvgIcons';
import { ComponentHelper, useEffectAsync } from '../../../../helper/Infra';
import { ICTaskLog } from '../../../../helper/Interfaces';
import { MergefloColors } from '../../../../helper/MergefloColors';
import { getTimeAmPm } from '../../../../helper/utils';
import EmptyElement from '../../../../pages/Dashboard/components/EmptyElement';
import CustomTag from '../../CustomTag/CustomTag';
import classes from '../TaskDetailModal.module.css';

type TaskDetailHistoryProps = {
  taskLogs: ICTaskLog[];
  taskId?: string;
};

const TaskDetailHistory: FC<TaskDetailHistoryProps> = ({
  taskLogs,
  taskId,
}) => {
  const [toggleDrawer, setToggleDrawer] = useState<boolean>(false);
  const mergefloColors = new MergefloColors();
  const componentHelper = new ComponentHelper();
  const container = useRef<HTMLDivElement>(null);
  const [currentTaskLogs, setcurrentTaskLogs] = useState<ICTaskLog[]>(taskLogs);

  const handleClickOutside = (event: any) => {
    if (container.current && !container.current.contains(event.target)) {
      setToggleDrawer(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });

  useEffectAsync(async () => {
    if (taskId) {
      const logs = (await componentHelper.listTaskLogs(taskId)) as ICTaskLog[];
      setcurrentTaskLogs(logs);
    }
  }, [taskId, taskLogs]);

   const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme, title }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: title === 'Copied!' ? '#00875A' : '#0E0942',
      color: ' #F4F4F7;',
      width: 'fit-content',
      fontSize: 14,
      height: 25,
      borderRadius: 29,
      textAlign: 'center',
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  }));

  return (
    <div ref={container}>
      <HtmlTooltip title={'Task Version History'} placement='top'>
      <IconButton
        size="small"
        onClick={() => setToggleDrawer(true)}
        className={classes.headerIcon}
      >
        <Icons.History />
      </IconButton>
      </HtmlTooltip>
      <div
        className={[
          classes.customDrawer,
          toggleDrawer ? classes.customDrawerActive : '',
        ].join(' ')}
      >
        <div className={'DrawerMenuHeader'}>
          <div className={'DrawerMenuTitle'}>Version History</div>
          <IconButton
            onClick={() => setToggleDrawer(false)}
            sx={{
              width: 38,
              height: 38,
            }}
          >
            <Icons.Cancel color="#1C174D" />
          </IconButton>
        </div>
        {taskLogs?.length > 0 ? (
          <div className={'DrawerMenuContent'}>
            {currentTaskLogs
              ?.sort((a, b) =>
                new Date(a.createdAt!) > new Date(b.createdAt!) ? -1 : 1
              )
              .map((log, index, arr) => {
                const logDate = new Date(log.createdAt).toLocaleDateString();
                const today = new Date().toLocaleDateString();
                const previousDay = new Date();
                previousDay.setDate(previousDay.getDate() - 1);
                const yesterday = previousDay.toLocaleDateString();
                const date =
                  logDate === today
                    ? 'Today'
                    : logDate === yesterday
                    ? 'Yesterday'
                    : logDate;
                const isDivided =
                  index === 0 ||
                  logDate !==
                    new Date(arr[index - 1]!.createdAt).toLocaleDateString();

                return (
                  <div key={index}>
                    <Divider
                      style={{
                        marginBottom: 20,
                        marginTop: 20,
                        display: isDivided ? '' : 'none',
                      }}
                    >
                      <span className="DividerText">{date}</span>
                    </Divider>

                    <div className={'TaskLogItem'}>
                      <div className={'TaskLogItemContent'}>
                        <CustomTag
                          color={mergefloColors.getUserColors(log.username)}
                          textColor="#ffffff"
                          text={'@' + log.username}
                          display={'inline-block'}
                        />
                        <div
                          dangerouslySetInnerHTML={{
                            __html: log.description,
                          }}
                        ></div>
                      </div>
                      <div
                        className={'TaskLogItemDate'}
                        style={{ justifyContent: 'flex-end' }}
                      >
                        <CustomTag
                          color="#E0DFE6"
                          textColor="#1C174D"
                          text={getTimeAmPm(log.createdAt)}
                          display={'inline-block'}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        ) : (
          <div className={classes.emptyContent}>
            <EmptyElement />
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskDetailHistory;
